import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ChevronRight, ChevronLeft, X, Sparkles,
  Database, ShoppingCart, FileSpreadsheet, Bot,
  Calendar, TrendingUp, ClipboardList, Settings, Rocket
} from "lucide-react";
import { Button } from "@/components/ui/button";

const TOUR_STEPS = [
  {
    target: '[data-testid="nav-materials"]',
    fallbackTarget: 'a[href="/"]',
    title: "Materials Dashboard",
    description: "Your home screen. Browse, search, and filter your SAP material master data in real time. Click any material card for detailed information.",
    icon: Database,
    position: "bottom",
  },
  {
    target: '[data-testid="nav-sales-orders"]',
    fallbackTarget: 'a[href="/sales-orders"]',
    title: "Sales Orders",
    description: "View all open sales order items with customer, delivery, and status information. Use filters to narrow results and export to Excel.",
    icon: ShoppingCart,
    position: "bottom",
  },
  {
    target: '[data-testid="nav-planning-report"]',
    fallbackTarget: 'a[href="/production-planning"]',
    title: "Production Planning Report",
    description: "A unified view combining Sales Orders, Business Partners, and Production Orders. See which items have production orders and their status.",
    icon: FileSpreadsheet,
    position: "bottom",
  },
  {
    target: '[data-testid="nav-planning-agent"]',
    fallbackTarget: 'a[href="/planning-agent"]',
    title: "AI Planning Agent",
    description: "Chat with your AI assistant! Ask about demand analysis, BOM explosions, availability checks, forecasts, and what-if scenarios — all using live SAP data.",
    icon: Bot,
    position: "bottom",
  },
  {
    target: '[data-testid="nav-scheduling"]',
    fallbackTarget: 'a[href="/scheduling"]',
    title: "Production Scheduling",
    description: "Generate optimized schedules from sales orders. Visualize with a Gantt chart, run what-if scenarios, and create production orders directly in SAP.",
    icon: Calendar,
    position: "bottom",
  },
  {
    target: '[data-testid="nav-forecasting"]',
    fallbackTarget: 'a[href="/forecasting"]',
    title: "Demand Forecasting",
    description: "Run statistical and AI-powered demand forecasts. Get monthly projections, trend analysis, confidence levels, and actionable recommendations.",
    icon: TrendingUp,
    position: "bottom",
  },
  {
    target: '[data-testid="nav-po-tracker"]',
    fallbackTarget: 'a[href="/production-order-tracker"]',
    title: "PO Tracker",
    description: "Track all production orders in one place. Monitor status, release individual or batch orders, and export the full list to Excel.",
    icon: ClipboardList,
    position: "bottom",
  },
  {
    target: '[data-testid="nav-settings"]',
    fallbackTarget: 'a[href="/settings"]',
    title: "Settings (Admin)",
    description: "Manage SAP systems, endpoints, connectivity tests, and credentials. Users, AI configuration, organizations, and audit history now have separate admin pages.",
    icon: Settings,
    position: "bottom",
    adminOnly: true,
  },
];

const STORAGE_KEY = "sap_datapulse_tour_completed";

const TourOverlay = ({ step, stepIndex, totalSteps, onNext, onPrev, onSkip }) => {
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const el = document.querySelector(step.target) || document.querySelector(step.fallbackTarget);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect(r);
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [step]);

  if (!rect) return null;

  const padding = 6;
  const spotlightStyle = {
    position: "fixed",
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    borderRadius: 10,
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
    zIndex: 9998,
    pointerEvents: "none",
    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const tooltipTop = rect.bottom + 16;
  const tooltipLeft = Math.max(12, Math.min(rect.left + rect.width / 2 - 180, window.innerWidth - 384));

  const Icon = step.icon;
  const isLast = stepIndex === totalSteps - 1;

  return createPortal(
    <>
      {/* Spotlight cutout */}
      <div style={spotlightStyle} />

      {/* Clickable overlay to prevent interaction */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 9997, cursor: "default" }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[9999] w-[360px] bg-popover border border-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
        style={{ top: tooltipTop, left: tooltipLeft }}
        data-testid="tour-tooltip"
      >
        {/* Progress bar */}
        <div className="h-1 bg-secondary">
          <div
            className="h-1 bg-primary transition-all duration-500"
            style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>

        <div className="p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-base leading-tight">{step.title}</h3>
              <span className="text-xs text-muted-foreground">
                Step {stepIndex + 1} of {totalSteps}
              </span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
        </div>

        <div className="px-5 pb-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onSkip} className="text-xs text-muted-foreground" data-testid="tour-skip">
            Skip Tour
          </Button>
          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <Button variant="outline" size="sm" onClick={onPrev} data-testid="tour-prev">
                <ChevronLeft className="w-4 h-4 mr-1" />Back
              </Button>
            )}
            <Button size="sm" onClick={onNext} data-testid="tour-next">
              {isLast ? (
                <>Finish <Rocket className="w-4 h-4 ml-1" /></>
              ) : (
                <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const OnboardingTour = ({ isAdmin = false, forceStart = false, onComplete }) => {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const steps = TOUR_STEPS.filter((s) => !s.adminOnly || isAdmin);

  useEffect(() => {
    if (forceStart) {
      setStepIndex(0);
      setActive(true);
      return;
    }
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      const timer = setTimeout(() => setActive(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [forceStart]);

  const finish = useCallback(() => {
    setActive(false);
    localStorage.setItem(STORAGE_KEY, "true");
    onComplete?.();
  }, [onComplete]);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) setStepIndex((i) => i + 1);
    else finish();
  };
  const handlePrev = () => { if (stepIndex > 0) setStepIndex((i) => i - 1); };
  const handleSkip = () => finish();

  if (!active || steps.length === 0) return null;

  return (
    <TourOverlay
      step={steps[stepIndex]}
      stepIndex={stepIndex}
      totalSteps={steps.length}
      onNext={handleNext}
      onPrev={handlePrev}
      onSkip={handleSkip}
    />
  );
};

export { OnboardingTour, STORAGE_KEY };
export default OnboardingTour;
